import React from "react";
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Typography,
  FormControlLabel,
  Checkbox,
  Chip,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { PhraseStatus } from "../../api/phrasebook";
import {
  LanguageCode,
  languageCodeToLabel,
} from "../../pages/playground/types";

interface Brand {
  id: string;
  name: string;
}

interface SearchFiltersProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedBrandIds: string[];
  setSelectedBrandIds: (brandIds: string[]) => void;
  brands: Brand[];
  onSearch: () => void;
  onRefresh: () => void;
  loading: boolean;
  totalCount: number;
  displayCount: number;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  languageFilter: string;
  setLanguageFilter: (language: string) => void;
  usedPhrasesOnly: boolean;
  setUsedPhrasesOnly: (used: boolean) => void;
  suitabilityFilter: string;
  setSuitabilityFilter: (filter: string) => void;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({
  searchQuery,
  setSearchQuery,
  selectedBrandIds,
  setSelectedBrandIds,
  brands,
  onSearch,
  loading,
  totalCount,
  displayCount,
  statusFilter,
  setStatusFilter,
  languageFilter,
  setLanguageFilter,
  usedPhrasesOnly,
  setUsedPhrasesOnly,
  suitabilityFilter,
  setSuitabilityFilter,
}) => {
  const handleBrandToggle = (brandId: string) => {
    if (selectedBrandIds.includes(brandId)) {
      setSelectedBrandIds(selectedBrandIds.filter((id) => id !== brandId));
    } else {
      setSelectedBrandIds([...selectedBrandIds, brandId]);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          placeholder="Search phrases..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch()}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
          sx={{ flex: 1, minWidth: 200 }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value={PhraseStatus.UNREVIEWED}>Unreviewed</MenuItem>
            <MenuItem value={PhraseStatus.INTERNALLY_REVIEWED}>
              Internally Reviewed
            </MenuItem>
            <MenuItem value={PhraseStatus.EXTERNALLY_REVIEWED}>
              Externally Reviewed
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Original Language</InputLabel>
          <Select
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            label="Original Language"
          >
            <MenuItem value="">All Languages</MenuItem>
            {Object.values(LanguageCode).map((langCode) => (
              <MenuItem key={langCode} value={langCode}>
                {languageCodeToLabel(langCode)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Suitable For</InputLabel>
          <Select
            value={suitabilityFilter}
            onChange={(e) => setSuitabilityFilter(e.target.value)}
            label="Suitable For"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="title">Title</MenuItem>
            <MenuItem value="description">Description</MenuItem>
            <MenuItem value="bullet_points">Bullet Points</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={usedPhrasesOnly}
              onChange={(e) => setUsedPhrasesOnly(e.target.checked)}
              size="small"
            />
          }
          label="Used phrases only"
          sx={{ ml: 1 }}
        />

        <Button
          variant="contained"
          onClick={onSearch}
          startIcon={<Search />}
          disabled={loading}
        >
          Search
        </Button>
      </Box>

      {/* Brands section at the bottom */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Brands:
          </Typography>
          {brands?.map((brand) => (
            <Chip
              key={brand.id}
              label={brand.name}
              size="small"
              clickable
              variant={
                selectedBrandIds.includes(brand.id) ? "filled" : "outlined"
              }
              color={
                selectedBrandIds.includes(brand.id) ? "primary" : "default"
              }
              onClick={() => handleBrandToggle(brand.id)}
            />
          ))}
          {selectedBrandIds.length > 0 && (
            <Chip
              label="Clear All"
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => setSelectedBrandIds([])}
            />
          )}
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary">
        Showing {displayCount} of {totalCount} phrases
        {selectedBrandIds.length > 0 &&
          ` for ${selectedBrandIds.length} selected brand${
            selectedBrandIds.length !== 1 ? "s" : ""
          }`}
      </Typography>
    </Box>
  );
};

export default SearchFilters;
